/**
 * BookingSuccess - Success confirmation component after booking.
 * Displays confirmed meeting details and provides calendar/reschedule options.
 */

import {
  Calendar,
  CheckCircle,
  ChevronDown,
  Clock,
  Download01,
  File04,
  Mail01,
  MessageChatSquare,
  RefreshCcw01,
  User01,
  VideoRecorder,
  XCircle,
} from '@untitledui/icons';
import type { ComponentType, ReactNode } from 'react';
import { useCallback } from 'react';

import type { PublicBookingType } from '@/lib/api/types/scheduling';
import { Button } from '@/lib/base/buttons/button';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { shouldShowHostIdentity } from '@/lib/scheduling/hostIdentity';
import { cx } from '@/utils/cx';

// ============================================================================
// Types
// ============================================================================

/**
 * Booking data for the success component.
 * This matches the shape we have after a successful booking.
 */
export interface BookingSuccessData {
  /** Booking entity ID */
  entity_id: string;
  /** Booker's name */
  booker_name: string;
  /** Booker's email */
  booker_email: string;
  /** Start time in ISO 8601 format */
  start_time: string;
  /** End time in ISO 8601 format */
  end_time: string;
  /** IANA timezone */
  timezone: string;
  /** Meeting link (if applicable) */
  meeting_link?: string | null;
  /** Cancellation token for cancel/reschedule */
  cancel_token?: string;
  /** Additional notes */
  notes?: string;
  /** Additional guest emails */
  additional_guests?: string[];
}

export interface BookingSuccessProps {
  /** The booking data */
  booking: BookingSuccessData;
  /** The meeting type details */
  meetingType: PublicBookingType;
  /** Base URL for cancel/reschedule links (defaults to window.location.origin) */
  baseUrl?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Formats a date for display.
 */
function formatDate(isoString: string, timezone: string): string {
  const date = new Date(isoString);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: timezone,
  }).format(date);
}

/**
 * Formats a time range for display.
 */
function formatTimeRange(
  startIso: string,
  endIso: string,
  timezone: string
): string {
  const startDate = new Date(startIso);
  const endDate = new Date(endIso);
  const formatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  });
  return `${formatter.format(startDate)} - ${formatter.format(endDate)}`;
}

/**
 * Escapes text for iCalendar format.
 */
function escapeIcsText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;')
    .replace(/\n/g, '\\n');
}

/**
 * Formats a date for iCalendar (YYYYMMDDTHHMMSSZ format).
 */
function formatIcsDate(isoString: string): string {
  const date = new Date(isoString);
  return date
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
}

/**
 * Generates an iCalendar (.ics) file content.
 */
function generateIcsContent(
  booking: BookingSuccessData,
  meetingType: PublicBookingType
): string {
  const startDate = formatIcsDate(booking.start_time);
  const endDate = formatIcsDate(booking.end_time);
  const now = formatIcsDate(new Date().toISOString());
  const uid = `${booking.entity_id}@ahoy.so`;

  const title = escapeIcsText(meetingType.display_name || meetingType.name);
  const description = escapeIcsText(
    [
      meetingType.description,
      booking.meeting_link ? `Join: ${booking.meeting_link}` : null,
      booking.notes ? `Notes: ${booking.notes}` : null,
    ]
      .filter(Boolean)
      .join('\\n\\n')
  );

  const location = booking.meeting_link
    ? escapeIcsText(booking.meeting_link)
    : '';

  const organizer = `ORGANIZER;CN=${escapeIcsText(meetingType.owner_username)}:mailto:noreply@ahoy.so`;

  // Build attendee list
  const attendees = [
    `ATTENDEE;CN=${escapeIcsText(booking.booker_name)};RSVP=TRUE:mailto:${booking.booker_email}`,
    ...(booking.additional_guests || []).map(
      guest => `ATTENDEE;RSVP=TRUE:mailto:${guest}`
    ),
  ].join('\r\n');

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Ahoy//Scheduling//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:REQUEST',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${startDate}`,
    `DTEND:${endDate}`,
    `SUMMARY:${title}`,
    description ? `DESCRIPTION:${description}` : '',
    location ? `LOCATION:${location}` : '',
    organizer,
    attendees,
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'BEGIN:VALARM',
    'TRIGGER:-PT15M',
    'ACTION:DISPLAY',
    `DESCRIPTION:${title} starts in 15 minutes`,
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ]
    .filter(line => line !== '')
    .join('\r\n');
}

/**
 * Builds a Google Calendar "add event" URL. Opens a pre-filled Google
 * Calendar template in a new tab — the user clicks Save once. Works for
 * any Google account without an OAuth handshake.
 */
function buildGoogleCalendarUrl(
  booking: BookingSuccessData,
  meetingType: PublicBookingType
): string {
  const fmt = (iso: string) =>
    new Date(iso)
      .toISOString()
      .replace(/[-:]/g, '')
      .replace(/\.\d{3}/, '');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: meetingType.display_name || meetingType.name,
    dates: `${fmt(booking.start_time)}/${fmt(booking.end_time)}`,
    details: [
      meetingType.description,
      booking.meeting_link ? `Join: ${booking.meeting_link}` : null,
      booking.notes ? `Notes: ${booking.notes}` : null,
    ]
      .filter(Boolean)
      .join('\n\n'),
    location: booking.meeting_link ?? '',
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Builds an Outlook (web) "add event" URL. Same shape as Google's
 * template — opens a pre-filled event in a new tab.
 */
function buildOutlookCalendarUrl(
  booking: BookingSuccessData,
  meetingType: PublicBookingType
): string {
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: meetingType.display_name || meetingType.name,
    startdt: booking.start_time,
    enddt: booking.end_time,
    body: [
      meetingType.description,
      booking.meeting_link ? `Join: ${booking.meeting_link}` : null,
      booking.notes ? `Notes: ${booking.notes}` : null,
    ]
      .filter(Boolean)
      .join('\n\n'),
    location: booking.meeting_link ?? '',
  });
  return `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`;
}

/**
 * Downloads a file with the given content.
 */
function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Returns a human-readable location type.
 */
function getLocationLabel(locationType: string): string {
  switch (locationType) {
    case 'google_meet':
      return 'Google Meet';
    case 'zoom':
      return 'Zoom';
    case 'phone':
      return 'Phone call';
    case 'in_person':
      return 'In person';
    default:
      return 'Video conference';
  }
}

// ============================================================================
// Component
// ============================================================================

export function BookingSuccess({
  booking,
  meetingType,
  baseUrl = typeof window !== 'undefined' ? window.location.origin : '',
}: BookingSuccessProps) {
  // Format display values
  const formattedDate = formatDate(booking.start_time, booking.timezone);
  const formattedTime = formatTimeRange(
    booking.start_time,
    booking.end_time,
    booking.timezone
  );
  // Friendlier TZ label — "Pacific/Honolulu" → "Honolulu (HST)" — by
  // pulling the city out of the IANA path and asking Intl for the
  // localised short abbreviation. Falls back to the raw label if the
  // abbreviation lookup fails. Replaces the technical-looking raw IANA
  // string the booker saw before.
  const friendlyTimezone = (() => {
    const raw = booking.timezone;
    const city = raw.split('/').pop()?.replace(/_/g, ' ') ?? raw;
    try {
      const parts = new Intl.DateTimeFormat(undefined, {
        timeZone: raw,
        timeZoneName: 'short',
      }).formatToParts(new Date(booking.start_time));
      const abbr = parts.find(p => p.type === 'timeZoneName')?.value;
      return abbr ? `${city} (${abbr})` : city;
    } catch {
      return raw.replace(/_/g, ' ');
    }
  })();
  // Note: friendlyTimezone above replaces the prior raw timezoneDisplay.

  // Build cancel/reschedule URLs. These MUST match the registered
  // BOOKING_ROUTE_PATHS in `lib/scheduling/bookingHosts.ts`:
  //   cancelBooking:     '/booking/:cancelToken/cancel'
  //   rescheduleBooking: '/booking/:cancelToken/reschedule'
  // Previous values (`/schedule/cancel/<token>` and `/schedule/reschedule/<token>`)
  // didn't match any route; they were swallowed by the `/schedule/:username/:slug`
  // pattern, which then failed with "Meeting Not Found" because no booking
  // type exists with username="cancel" or "reschedule".
  const cancelUrl = booking.cancel_token
    ? `${baseUrl}/booking/${booking.cancel_token}/cancel`
    : null;
  const rescheduleUrl = booking.cancel_token
    ? `${baseUrl}/booking/${booking.cancel_token}/reschedule`
    : null;

  // Handlers for each calendar target. Google/Outlook open a pre-filled
  // template in a new tab (one-click add for the most common providers).
  // Apple, Yahoo, and "other" fall back to a downloadable .ics file —
  // double-click the file and the OS's default calendar imports it.
  const handleAddToGoogle = useCallback(() => {
    window.open(
      buildGoogleCalendarUrl(booking, meetingType),
      '_blank',
      'noopener,noreferrer'
    );
  }, [booking, meetingType]);

  const handleAddToOutlook = useCallback(() => {
    window.open(
      buildOutlookCalendarUrl(booking, meetingType),
      '_blank',
      'noopener,noreferrer'
    );
  }, [booking, meetingType]);

  const handleDownloadIcs = useCallback(() => {
    const icsContent = generateIcsContent(booking, meetingType);
    const filename = `${meetingType.slug || 'meeting'}-${booking.entity_id.slice(0, 8)}.ics`;
    downloadFile(icsContent, filename, 'text/calendar;charset=utf-8');
  }, [booking, meetingType]);

  const handleCalendarAction = useCallback(
    (key: React.Key) => {
      switch (key) {
        case 'google':
          handleAddToGoogle();
          break;
        case 'outlook':
          handleAddToOutlook();
          break;
        case 'ics':
        default:
          handleDownloadIcs();
      }
    },
    [handleAddToGoogle, handleAddToOutlook, handleDownloadIcs]
  );

  // Multi-host scheduling types don't have a single visible host on the
  // booking page; suppressing the host row here keeps the confirmation
  // consistent with what the booker saw when they picked the slot.
  const showHostIdentity = shouldShowHostIdentity(meetingType);
  const hostDisplayName =
    meetingType.owner_display_name || meetingType.owner_username;
  const additionalGuestCount = booking.additional_guests?.length ?? 0;

  const hasFooterActions = Boolean(rescheduleUrl || cancelUrl);

  return (
    <div className='mx-auto flex max-w-xl flex-col gap-6'>
      {/* Status + title — left-aligned to match a product receipt rather
          than a celebratory landing page. Chip + title sit on the same
          axis as the metadata below, giving the page a single reading
          column instead of a centred / left-aligned split. */}
      <div className='flex flex-col gap-2'>
        <span className='inline-flex w-fit items-center gap-1.5 rounded-full bg-[var(--color-success-50)] px-2.5 py-1 text-xs font-medium text-[var(--color-text-success-primary)]'>
          <CheckCircle className='size-3.5' aria-hidden='true' />
          Confirmed
        </span>
        <h1 className='text-2xl font-semibold leading-tight text-[var(--color-text-primary)]'>
          {meetingType.display_name || meetingType.name}
        </h1>
        <p className='text-sm text-[var(--color-text-secondary)]'>
          We sent a confirmation to{' '}
          <span className='font-medium text-[var(--color-text-primary)]'>
            {booking.booker_email}
          </span>
          .
        </p>
      </div>

      {/* Meeting details — flat icon-led rows, matching BookingHostCard's
          metadata idiom. No nested card border. */}
      <div className='flex flex-col gap-3 border-t border-[var(--color-border-secondary)] pt-6'>
        <DetailRow icon={Calendar} primary={formattedDate} />
        <DetailRow
          icon={Clock}
          primary={formattedTime}
          secondary={friendlyTimezone}
        />
        <DetailRow
          icon={VideoRecorder}
          primary={getLocationLabel(meetingType.location_type)}
        >
          {booking.meeting_link && (
            // Join link sits directly under the location, indented into the
            // same column as the secondary metadata text. Previously it
            // floated to the far right via space-between, leaving "Google
            // Meet" and "Join meeting →" visually disconnected.
            <a
              href={booking.meeting_link}
              target='_blank'
              rel='noopener noreferrer'
              className='mt-0.5 inline-flex items-center gap-1 text-sm font-medium text-fg-brand-primary hover:underline'
            >
              Join meeting →
            </a>
          )}
        </DetailRow>
        {showHostIdentity && (
          <DetailRow icon={User01} primary={hostDisplayName} />
        )}
        <DetailRow
          icon={Mail01}
          primary={booking.booker_email}
          secondary={
            additionalGuestCount > 0
              ? `+${additionalGuestCount} guest${additionalGuestCount === 1 ? '' : 's'}`
              : undefined
          }
        />
        {booking.notes && (
          // Notes rendered inline under the chat icon — the icon already
          // signals "notes", so a redundant "Notes" label was just visual
          // weight. The booker's text reads as a small quoted block.
          <DetailRow icon={MessageChatSquare} primary={booking.notes} />
        )}
      </div>

      {/* Actions — Add to calendar is the primary next step; Reschedule and
          Cancel are quiet inline links. When the cancel token is absent
          (e.g. for an embed that hides those flows), Add to calendar is
          flush right on its own instead of leaving an empty left side. */}
      <div
        className={cx(
          'flex flex-wrap items-center gap-4 border-t border-[var(--color-border-secondary)] pt-6',
          hasFooterActions ? 'justify-between' : 'justify-end'
        )}
      >
        {hasFooterActions && (
          <div className='flex items-center gap-4'>
            {rescheduleUrl && (
              <a
                href={rescheduleUrl}
                className='inline-flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]'
              >
                <RefreshCcw01 className='size-4' />
                Reschedule
              </a>
            )}
            {cancelUrl && (
              <a
                href={cancelUrl}
                className='inline-flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-error-primary)]'
              >
                <XCircle className='size-4' />
                Cancel
              </a>
            )}
          </div>
        )}
        {/* Multi-target Add to Calendar — Google + Outlook open a
            pre-filled compose URL in a new tab (one click); Apple /
            Yahoo / other fall back to a downloadable .ics file. Matches
            the Cal.com / Calendly pattern; a single .ics button forced
            the booker through an extra import step. */}
        <Dropdown.Root>
          <Button
            color='primary'
            size='md'
            iconLeading={Download01}
            iconTrailing={ChevronDown}
          >
            Add to calendar
          </Button>
          <Dropdown.Popover>
            <Dropdown.Menu onAction={handleCalendarAction}>
              <Dropdown.Item
                id='google'
                label='Google Calendar'
                icon={Calendar}
              />
              <Dropdown.Item id='outlook' label='Outlook' icon={Calendar} />
              <Dropdown.Separator />
              <Dropdown.Item
                id='ics'
                label='Apple / other (.ics)'
                icon={File04}
              />
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown.Root>
      </div>
    </div>
  );
}

interface DetailRowProps {
  icon: ComponentType<{ className?: string }>;
  primary: ReactNode;
  secondary?: ReactNode;
  /** Optional content rendered below `primary` (e.g. a join-meeting link). */
  children?: ReactNode;
}

function DetailRow({
  icon: Icon,
  primary,
  secondary,
  children,
}: DetailRowProps) {
  return (
    <div className='flex items-start gap-3'>
      <Icon
        className='mt-0.5 size-4 shrink-0 text-[var(--color-text-tertiary)]'
        aria-hidden='true'
      />
      <div className='min-w-0 flex-1 text-sm'>
        <div className='whitespace-pre-line text-[var(--color-text-primary)]'>
          {primary}
        </div>
        {secondary && (
          <div className='text-[var(--color-text-tertiary)]'>{secondary}</div>
        )}
        {children}
      </div>
    </div>
  );
}
